import { Link } from "react-router";
import { useLingui } from "@lingui/react";
import { ChevronRightIcon, UsersIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { BlockSource } from "@/hooks/useDashboardSummary";
import type { shifts_ShiftListResponse } from "@/api/models/shifts_ShiftListResponse";
import type { group_presence_OpenGroupPresenceResponse } from "@/api/models/group_presence_OpenGroupPresenceResponse";
import { onShiftNow, ON_SHIFT_NAMES_SHOWN, type OnShiftPerson } from "@/lib/analytics/onShiftNow";
import { formatOrgTime } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { shiftsScheduleUrl } from "@/components/cockpit/explorer/lib/drillThrough";

interface OnShiftNowRowProps {
  shifts: BlockSource<shifts_ShiftListResponse>;
  presence: BlockSource<group_presence_OpenGroupPresenceResponse>;
  /** The Dashboard's group scope; undefined without a filter. */
  inScope?: (groupId: string) => boolean;
}

function minutesNow(): number {
  const [h, m] = formatOrgTime(new Date().toISOString()).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * "Who is here right now" (#2175 C5, the phone's OnShiftNowRow): the first
 * three people whose shift covers the current minute, each with their group,
 * then "+N more", opening the shift planner. With group presence recorded
 * (#2114) a dot marks who is in the group; a person on shift without a mark
 * reads "not in the group". The plan decides who is listed. Rendered inside
 * the live staffing card, whose minute refresh keeps the time current.
 */
export function OnShiftNowRow({ shifts, presence, inScope }: OnShiftNowRowProps) {
  const { i18n } = useLingui();
  // A failed presence read leaves the plan alone, as on the phone.
  const inGroup = presence.data?.group_presence_enabled
    ? new Set(presence.data.open.map((p) => p.employee_id))
    : undefined;
  const people = onShiftNow(shifts.data?.shifts, minutesNow(), { inScope, inGroup });
  const shown = people.slice(0, ON_SHIFT_NAMES_SHOWN);
  const more = people.length - shown.length;

  const personLabel = (p: OnShiftPerson) =>
    p.group
      ? i18n._({ id: "dashboard.onShift.person", message: "{name} ({group})", values: { name: p.name, group: p.group } })
      : p.name;

  if (shifts.isLoading) return <Skeleton className="h-9 w-full" />;
  if (shifts.isError) {
    return <QueryErrorState compact error={shifts.error} onRetry={() => void shifts.refetch()} />;
  }

  return (
    <Link
      to={shiftsScheduleUrl()}
      data-testid="dashboard-on-shift-now"
      className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <UsersIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {people.length === 0 ? (
          <span className="text-muted-foreground">
            {i18n._({ id: "dashboard.onShift.nobody", message: "Nobody on shift right now" })}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {shown.map((p) => (
              <span key={p.employeeId} className="inline-flex items-center gap-1.5" data-testid="dashboard-on-shift-person">
                {p.inGroup !== null && (
                  <span
                    className={cn("size-2 shrink-0 rounded-full", p.inGroup ? "bg-success-on-surface" : "border border-muted-foreground")}
                    aria-hidden="true"
                  />
                )}
                <span>
                  {p.inGroup === false
                    ? i18n._({
                        id: "dashboard.onShift.notInGroup",
                        message: "{person}, not in the group",
                        values: { person: personLabel(p) },
                      })
                    : personLabel(p)}
                </span>
                {p.inGroup === true && (
                  <span className="sr-only">
                    {i18n._({ id: "dashboard.onShift.inGroup", message: "in the group" })}
                  </span>
                )}
              </span>
            ))}
            {more > 0 && (
              <span className="text-muted-foreground tabular-nums">
                {i18n._({ id: "dashboard.onShift.more", message: "+{count} more", values: { count: more } })}
              </span>
            )}
          </span>
        )}
      </span>
      <span className="sr-only">
        {i18n._({ id: "dashboard.onShift.openPlanner", message: "Open the shift planner" })}
      </span>
      <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}
